import jsPDF from "jspdf";
import { supabase } from "../../lib/supabase";
import { logActivity } from "./driveClient";
import { fetchInvoiceSettings } from "./invoiceEngine";

const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_neon-beats-label/artifacts/as4oc6zo_INDEPENDENT_MUSIC_LABEL__1_-removebg-preview.png";
let _logoCache = null;
async function loadLogoDataUrl() {
  if (_logoCache) return _logoCache;
  try {
    const resp = await fetch(LOGO_URL, { mode: "cors" });
    const blob = await resp.blob();
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
    _logoCache = dataUrl;
    return dataUrl;
  } catch { return null; }
}

const formatEUR = (v) => (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const formatPercent = (v) => (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " %";

export function computeProvision(gross, percent) {
  const g = Number(gross || 0);
  const p = Number(percent || 0);
  const label = Math.round((g * p) * 100) / 10000; // rundet auf 2 Dezimalen
  const artist = Math.round((g - label) * 100) / 100;
  return { label, artist };
}

// ─── CRUD ────────────────────────────────────────────────────
export async function fetchProvisions(filter = {}) {
  let q = supabase
    .from("drive_provision_statements")
    .select("*, drive_artists(id, artist_name, first_name, last_name, iban, address, zip_city, bank_name, bic, email, commission_percent)")
    .order("month", { ascending: false })
    .order("created_at", { ascending: false });
  if (filter.artist_id) q = q.eq("artist_id", filter.artist_id);
  if (filter.month) q = q.eq("month", filter.month);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

async function nextProvisionNumber() {
  const { data, error } = await supabase.rpc("next_provision_number");
  if (error) throw error;
  return data;
}

function buildArtistSnapshot(a) {
  if (!a) return {};
  return {
    id: a.id,
    display_name: [a.first_name, a.last_name].filter(Boolean).join(" ") || a.artist_name,
    artist_name: a.artist_name,
    email: a.email,
    address: a.address,
    zip_city: a.zip_city,
    iban: a.iban,
    bic: a.bic,
    bank_name: a.bank_name,
  };
}

export async function createProvisionFromPayout({ payout, artist, commission_percent, gross_amount, notes }) {
  const pct = Number(commission_percent);
  const { label, artist: artistAmt } = computeProvision(gross_amount, pct);
  const number = await nextProvisionNumber();
  const settings = await fetchInvoiceSettings();

  const { data: row, error } = await supabase.from("drive_provision_statements").insert([{
    statement_number: number,
    month: payout?.month || null,
    musichub_id: payout?.id || null,
    artist_id: artist.id,
    artist_snapshot: buildArtistSnapshot(artist),
    source_label: payout?.title || payout?.file_name || null,
    source_date: payout?.created_at ? new Date(payout.created_at).toISOString().slice(0, 10) : null,
    gross_amount: Number(gross_amount),
    commission_percent: pct,
    label_amount: label,
    artist_amount: artistAmt,
    notes: notes || null,
    status: "issued",
  }]).select().single();
  if (error) throw error;

  // PDF bauen und hochladen
  const pdf = await renderProvisionPdf({ ...row, sender: settings, artist: buildArtistSnapshot(artist) });
  const blob = pdf.output("blob");
  const filename = `${number}.pdf`;
  const path = `provisions/${artist.id}/${Date.now()}-${filename}`;
  const up = await supabase.storage.from("drive-files").upload(path, blob, { contentType: "application/pdf", upsert: false });
  if (up.error) throw up.error;

  await supabase.from("drive_provision_statements").update({ storage_path: path }).eq("id", row.id);
  await logActivity("create_provision", { type: "provision", id: row.id, name: number });

  return { ...row, storage_path: path };
}

export async function deleteProvision(row) {
  if (row.storage_path) {
    await supabase.storage.from("drive-files").remove([row.storage_path]).catch(() => {});
  }
  const { error } = await supabase.from("drive_provision_statements").delete().eq("id", row.id);
  if (error) throw error;
  await logActivity("delete_provision", { type: "provision", id: row.id, name: row.statement_number });
}

export async function downloadProvision(row) {
  if (!row.storage_path) throw new Error("Keine Datei hinterlegt");
  const { data: blob, error } = await supabase.storage.from("drive-files").download(row.storage_path);
  if (error) throw error;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${row.statement_number}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export async function markProvisionPaid(id) {
  const { data, error } = await supabase.from("drive_provision_statements").update({ status: "paid", paid_at: new Date().toISOString() }).eq("id", id).select().single();
  if (error) throw error;
  return data;
}

// ═══════════════════════════════════════════════════════════════
// PDF-Rendering — Straßenwert-Bordeaux-Stil
// ═══════════════════════════════════════════════════════════════
export async function renderProvisionPdf(row) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const marginX = 20;
  const bannerH = 40;
  const contentW = pageW - 2 * marginX;

  const INK = [24, 24, 28];
  const MUTED = [110, 110, 118];
  const LINE = [220, 220, 224];
  const BORDEAUX = [128, 24, 38];
  const ACCENT = [180, 30, 42];
  const SOFT = [245, 245, 247];

  const logoData = await loadLogoDataUrl();
  const s = row.sender || {};
  const a = row.artist || row.artist_snapshot || {};

  // Banner
  pdf.setFillColor(...BORDEAUX);
  pdf.rect(0, 0, pageW, bannerH, "F");
  if (logoData) {
    try {
      const lw = 76, lh = 30;
      pdf.addImage(logoData, "PNG", (pageW - lw) / 2, (bannerH - lh) / 2, lw, lh);
    } catch { /* ignore */ }
  }

  let y = bannerH + 14;

  // Titel + Nummer (ohne Char-Spacing wegen Ausrichtungsproblemen mit langen Zahlen)
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(19);
  pdf.setCharSpace(0);
  pdf.setTextColor(...INK);
  pdf.text("PROVISIONSABRECHNUNG", marginX, y);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  pdf.text(`Nr. ${row.statement_number || ""}`, marginX, y + 5);

  pdf.setDrawColor(...ACCENT);
  pdf.setLineWidth(0.8);
  pdf.line(marginX, y + 2, marginX + 42, y + 2);
  pdf.setLineWidth(0.2);

  // Absender rechts
  pdf.setFontSize(7.5);
  pdf.setTextColor(...MUTED);
  const senderLines = [
    s.company_name || "Straßenwert Records",
    s.owner_name,
    s.address_street,
    [s.address_zip, s.address_city].filter(Boolean).join(" "),
  ].filter(Boolean);
  senderLines.forEach((ln, i) => pdf.text(ln, pageW - marginX, y - 4 + i * 3.5, { align: "right" }));

  y += 20;

  // Briefkopf
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...MUTED);
  const briefkopf = [s.company_name, s.address_street, `${s.address_zip || ""} ${s.address_city || ""}`.trim()].filter(Boolean).join(" | ");
  pdf.text(briefkopf, marginX, y);
  pdf.setDrawColor(...LINE);
  pdf.setLineWidth(0.2);
  pdf.line(marginX, y + 2, marginX + 90, y + 2);
  y += 8;

  // Empfänger + Meta
  const blockY = y;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...INK);
  pdf.text(a.display_name || a.artist_name || "—", marginX, y);
  y += 5;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  if (a.artist_name && a.display_name && a.artist_name !== a.display_name) {
    pdf.text(`Künstlername: ${a.artist_name}`, marginX, y); y += 4.5;
  }
  if (a.address) { pdf.text(a.address, marginX, y); y += 4.5; }
  if (a.zip_city) { pdf.text(a.zip_city, marginX, y); y += 4.5; }
  if (a.email) { pdf.setTextColor(...MUTED); pdf.text(a.email, marginX, y); y += 4.5; pdf.setTextColor(...INK); }

  const infoX = pageW - marginX - 72;
  let infoY = blockY;
  const infoRow = (label, val) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(label, infoX, infoY);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...INK);
    pdf.text(val || "—", pageW - marginX, infoY, { align: "right" });
    infoY += 5.5;
  };
  const [yr, mo] = (row.month || "").split("-");
  const monthLabel = yr && mo ? new Date(Number(yr), Number(mo) - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" }) : "—";
  infoRow("Abrechnungsdatum", new Date(row.issued_at || row.created_at || new Date()).toLocaleDateString("de-DE"));
  infoRow("Abrechnungszeitraum", monthLabel);
  infoRow("Kunden-Nr.", a.customer_number || "—");
  infoRow("Aussteller", s.company_name || "Straßenwert Records");

  y = Math.max(y, infoY) + 10;

  // Grundlage
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(...INK);
  pdf.text("Grundlage der Abrechnung", marginX, y);
  y += 5;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.setTextColor(...INK);
  const grundlage = `Diese Abrechnung basiert auf der Einnahmenmeldung ${row.source_label ? `"${row.source_label}"` : "(MusicHub Payout)"}${row.source_date ? ` vom ${new Date(row.source_date).toLocaleDateString("de-DE")}` : ""}. Die dort ausgewiesenen Einnahmen wurden auf das Geschaftskonto von ${s.company_name || "Straßenwert Records"} vereinnahmt und werden gemaß Management- und Exklusivvertretungsvertrag vom 16.02.2026, §7 (Provision & Abrechnung), auf die Vertragsparteien aufgeteilt.`
    .replace(/ä/g, "\u00e4").replace(/ö/g, "\u00f6").replace(/ü/g, "\u00fc").replace(/ß/g, "\u00df");
  const gLines = pdf.splitTextToSize(grundlage, contentW);
  for (const l of gLines) { pdf.text(l, marginX, y); y += 4.8; }
  y += 4;

  // Aufschlüsselung Tabelle (statt Box) für maximale Klarheit
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(...INK);
  pdf.text("Aufschlüsselung", marginX, y);
  y += 5;

  const tableTop = y;
  const rows = [
    { label: "Bruttoeinnahme (Basis für Abrechnung)", val: formatEUR(row.gross_amount), sign: "+" },
    { label: `Provisionsanspruch Management (${formatPercent(row.commission_percent)})`, val: formatEUR(row.label_amount), sign: "-", muted: true },
  ];

  // Header row
  pdf.setFillColor(...SOFT);
  pdf.rect(marginX, y - 4, contentW, 7, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7.5);
  pdf.setTextColor(...INK);
  pdf.text("POSITION", marginX + 4, y);
  pdf.text("BETRAG", pageW - marginX - 4, y, { align: "right" });
  y += 5;

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  for (const r of rows) {
    pdf.setDrawColor(...LINE);
    pdf.setLineWidth(0.15);
    pdf.line(marginX, y + 2, pageW - marginX, y + 2);
    pdf.setTextColor(...(r.muted ? MUTED : INK));
    pdf.text(r.label, marginX + 4, y);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...(r.muted ? MUTED : INK));
    pdf.text(`${r.sign} ${r.val}`, pageW - marginX - 4, y, { align: "right" });
    pdf.setFont("helvetica", "normal");
    y += 7;
  }

  // Summenzeile
  y += 2;
  pdf.setDrawColor(...INK);
  pdf.setLineWidth(0.5);
  pdf.line(marginX, y - 3, pageW - marginX, y - 3);
  pdf.setFillColor(255, 240, 240);
  pdf.rect(marginX, y - 2, contentW, 10, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(...ACCENT);
  pdf.text("Auszahlungsbetrag an Künstler", marginX + 4, y + 4);
  pdf.setFontSize(13);
  pdf.text(formatEUR(row.artist_amount), pageW - marginX - 4, y + 4, { align: "right" });
  y += 16;

  // Notizen
  if (row.notes) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    pdf.setTextColor(...MUTED);
    const nLines = pdf.splitTextToSize(`Anmerkung: ${row.notes}`, contentW);
    for (const l of nLines) { pdf.text(l, marginX, y); y += 5; }
    y += 3;
  }

  // Zahlungshinweis
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(...INK);
  pdf.text("Zahlung", marginX, y);
  y += 5;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...INK);
  pdf.text(`Zahlungsziel: 7 Werktage ab Rechnungsdatum (${new Date(row.issued_at || row.created_at || new Date()).toLocaleDateString("de-DE")})`, marginX, y); y += 4.5;
  pdf.text(`Verwendungszweck: ${row.statement_number} ${a.artist_name || a.display_name || ""} ${monthLabel}`.trim(), marginX, y); y += 4.5;
  if (a.iban) { pdf.text(`Empfänger-IBAN: ${a.iban}`, marginX, y); y += 4.5; }
  if (a.bic) { pdf.text(`BIC: ${a.bic}`, marginX, y); y += 4.5; }
  if (a.bank_name) { pdf.text(`Bank: ${a.bank_name}`, marginX, y); y += 4.5; }
  if (!a.iban) {
    pdf.setTextColor(...ACCENT);
    pdf.text("Hinweis: Keine IBAN im Künstler-Datensatz hinterlegt.", marginX, y);
    pdf.setTextColor(...INK);
    y += 4.5;
  }

  y += 6;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(...INK);
  pdf.text("Rechtliche Hinweise", marginX, y);
  y += 5;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED);
  const legal = [
    "1. Rechtsgrundlage: Management- und Exklusivvertretungsvertrag zwischen Maurizio Rogait (Manager) und dem Künstler, §7 – Provision & Abrechnung.",
    "2. Diese Provisionsabrechnung stellt eine interne Verrechnung im Rahmen des Managementvertrags dar und ersetzt eine gesonderte Rechnungsstellung durch den Künstler.",
    "3. Umsatzsteuer wird gemäß Vertrag nicht gesondert ausgewiesen; jede Vertragspartei ist für ihre eigenen steuerlichen Pflichten selbst verantwortlich.",
    "4. Einwendungen gegen diese Abrechnung sind innerhalb von 14 Tagen nach Zugang schriftlich mitzuteilen. Nach Ablauf gilt die Abrechnung als anerkannt.",
    "5. Der Auszahlungsbetrag wird per SEPA-Überweisung auf das oben angegebene Konto überwiesen.",
  ];
  for (const line of legal) {
    const wrapped = pdf.splitTextToSize(line, contentW);
    for (const l of wrapped) { pdf.text(l, marginX, y); y += 3.8; }
    y += 0.5;
  }

  // Fußzeile
  const fy = pageH - 18;
  pdf.setDrawColor(...LINE);
  pdf.setLineWidth(0.25);
  pdf.line(marginX, fy - 6, pageW - marginX, fy - 6);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...MUTED);
  const cols = [
    [s.company_name, s.owner_name, s.address_street, `${s.address_zip || ""} ${s.address_city || ""}`.trim()].filter(Boolean),
    [s.email, s.phone, s.website].filter(Boolean),
    [s.tax_id ? `St.-Nr.: ${s.tax_id}` : null, s.vat_id ? `USt-IdNr.: ${s.vat_id}` : null].filter(Boolean),
    [s.bank_name, s.iban ? `IBAN: ${s.iban}` : null, s.bic ? `BIC: ${s.bic}` : null].filter(Boolean),
  ];
  const cw = contentW / 4;
  cols.forEach((lines, i) => {
    const x = marginX + cw * i;
    lines.forEach((ln, j) => pdf.text(ln, x, fy + j * 3.5));
  });
  pdf.setFontSize(6.5);
  pdf.text("Seite 1 / 1", pageW - marginX, pageH - 6, { align: "right" });

  return pdf;
}
